"use client";

import { Core } from "@faire/design-tokens";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { formatBytes } from "@faire/web/common/formatBytes";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import {
  backwardsCompatibleValues,
  useStrictLocalization,
} from "@faire/web/common/localization/localize";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import * as React from "react";
import Dropzone, { FileRejection } from "react-dropzone";

import { useIsSSR } from "../../common/server-side-rendering/isSSR";

import { ImageUploaderStatus } from "./ImageUploaderStatus";
import { DropzoneContainer, CentralIconWrapper, MaxSizeDiv } from "./styles";

interface IProps {
  onImageUpload: (imageFile: File) => Promise<void>;
  onUploadError: (message: string) => void;
  height: number;
  width: number;
  currentImage?: IImage;
  uploadCta?: string;
  loading?: boolean;
  error?: boolean;
  maxFileSize?: number;
  circular?: boolean;
  /**
   * This prop is used if the reupload functionality is controlled by an external button,
   * meaning the uploader should not be clickable if there is an image already uploaded.
   */
  externallyManagedReupload?: boolean;
  displayRestrictions?: boolean;
}

const ACCEPTED_FILES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".jpeg",
  ".jpg",
  ".png",
  ".webp",
  ".pdf",
  ".xlsx",
];

const MAX_FILE_SIZE = 10 * 1024 * 1024;

/**
 * @trackcomponent
 * @link [Docs](https://slate.faire.team/component/dropzone/web/dropzone)
 */
export const ImageUploaderDropzone: React.FC<IProps> = ({
  onImageUpload,
  onUploadError,
  height,
  width,
  currentImage,
  uploadCta,
  loading,
  error,
  maxFileSize,
  circular,
  externallyManagedReupload = false,
  displayRestrictions,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const maxFileSizeOrDefault: number = maxFileSize ?? MAX_FILE_SIZE;
  const showImage: boolean = !!currentImage && !loading;
  const showUploadError: boolean = !currentImage && !!error;
  const isSSR = useIsSSR();
  let iconColor: string;
  if (showImage) {
    iconColor = Core.icon.inverse;
  } else if (showUploadError) {
    iconColor = Core.icon.critical;
  } else {
    iconColor = Core.text.default;
  }

  const handleRejectedImage = (rejectedFile: FileRejection) => {
    const baseMessage = strictLocalize(
      {
        id: "OseXri",
        defaultMessage: "Error uploading {fileName}.",
        description: {
          text: "Indicating there was an error uploading a file",
        },
      },
      backwardsCompatibleValues({ fileName: rejectedFile.file.name })
    );
    if (rejectedFile.file.size > maxFileSizeOrDefault) {
      onUploadError(
        `${baseMessage} ${strictLocalize(
          {
            id: "NeuzEd",
            defaultMessage: "Please upload a file less than {fileSize}.",
            description: {
              text: "Telling brand to upload a file that is a smaller size",
            },
          },
          backwardsCompatibleValues({
            fileSize: formatBytes(maxFileSizeOrDefault, strictLocalize),
          })
        )}`
      );
    } else if (!ACCEPTED_FILES.includes(rejectedFile.file.type)) {
      onUploadError(
        `${baseMessage} ${strictLocalize(
          {
            id: "gsJdBO",
            defaultMessage: "{fileType} is not a supported file type.",
            description: {
              text: "Telling brand to upload a file that is a different file type",
            },
          },
          backwardsCompatibleValues({ fileType: rejectedFile.file.type })
        )}`
      );
    } else {
      onUploadError(
        `${baseMessage} ${strictLocalize({
          id: "LeFFG7",
          defaultMessage: "Please try again.",
          description: {
            text: "Telling brand to try uploading their file again",
          },
        })}`
      );
    }
  };

  const handleDrop = (
    acceptedFiles: File[],
    rejectedFiles: FileRejection[]
  ) => {
    acceptedFiles.forEach(async (acceptedFile) => {
      try {
        await onImageUpload(acceptedFile);
      } catch {
        onUploadError(
          strictLocalize(
            {
              id: "0ZOu5r",
              defaultMessage: "Error uploading {fileName}. Please try again.",
            },
            backwardsCompatibleValues(
              backwardsCompatibleValues({ fileName: acceptedFile.name })
            )
          )
        );
      }
    });

    rejectedFiles.forEach((rejectedFile) => handleRejectedImage(rejectedFile));
  };

  return (
    <Dropzone
      noClick={externallyManagedReupload && showImage}
      multiple={false}
      onDrop={handleDrop}
      accept={ACCEPTED_FILES}
      maxSize={maxFileSizeOrDefault}
    >
      {({ getRootProps, getInputProps }) => (
        <DropzoneContainer
          $externallyManagedClicks={externallyManagedReupload && showImage}
          $height={height}
          $width={width}
          $uploadError={showUploadError}
          $circleImage={circular}
        >
          <MaxSizeDiv {...getRootProps()}>
            <label htmlFor="imageUploader" style={{ display: "none" }}>
              <StrictLocalMsg id="b4HW4N" defaultMessage="Image Uploader" />
            </label>
            <input {...getInputProps()} id="imageUploader" />
            {showImage && currentImage ? (
              <img
                src={optimizeImageUrl(currentImage, isSSR)}
                alt={currentImage.alt_text}
              />
            ) : null}
            <CentralIconWrapper $color={iconColor} $defaultHidden={showImage}>
              <ImageUploaderStatus
                uploadCta={uploadCta}
                isLoading={loading}
                error={showUploadError}
                displayedMaxFileSize={
                  displayRestrictions ? maxFileSizeOrDefault : undefined
                }
                displayedFileTypes={
                  displayRestrictions
                    ? strictLocalize({
                        id: "yXXB1E",
                        defaultMessage: "JPEG, JPG, PNG, WEBP, PDF or XLSX",
                        description: {
                          text: "A list of file types the user can upload.",
                        },
                      })
                    : undefined
                }
              />
            </CentralIconWrapper>
          </MaxSizeDiv>
        </DropzoneContainer>
      )}
    </Dropzone>
  );
};
